import { z } from 'zod';

import {
  MAX_TRANSFER_CONTROL_BYTES,
  TRANSFER_CHUNK_FRAME,
  TRANSFER_FRAME_HEADER_BYTES,
  TRANSFER_FRAME_VERSION,
} from './constants.js';
import type { ProtocolIssue, ValidationResult } from './messages.js';
import { transferIdSchema, transferMessageSchema } from './schemas.js';

export const TRANSFER_MESSAGE_TYPES = [
  'transfer_offer',
  'transfer_accept',
  'transfer_reject',
  'file_complete',
  'transfer_done',
  'transfer_cancel',
  'transfer_error',
] as const;

const KNOWN_TRANSFER_TYPES = new Set<string>(TRANSFER_MESSAGE_TYPES);

export type TransferMessage = z.infer<typeof transferMessageSchema>;
export type TransferMessageType = TransferMessage['type'];

/** Upper bound for a single binary chunk frame, including the header. */
export const MAX_CHUNK_FRAME_BYTES = TRANSFER_FRAME_HEADER_BYTES + 256 * 1024;

export function validateTransferMessage(input: unknown): ValidationResult<TransferMessage> {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    return {
      ok: false,
      error: { code: 'VALIDATION_FAILED', message: 'Transfer messages must be JSON objects.' },
    };
  }

  const type = (input as Record<string, unknown>)['type'];
  if (typeof type !== 'string' || !KNOWN_TRANSFER_TYPES.has(type)) {
    return { ok: false, error: { code: 'UNKNOWN_MESSAGE', message: 'Unknown transfer message.' } };
  }

  const parsed = transferMessageSchema.safeParse(input);
  if (!parsed.success) {
    const issues: ProtocolIssue[] = parsed.error.issues.slice(0, 8).map((issue) => ({
      path: issue.path.join('.') || '(root)',
      message: issue.message.slice(0, 300),
    }));
    return {
      ok: false,
      error: {
        code: 'VALIDATION_FAILED',
        message: 'Transfer message did not match the schema.',
        issues,
      },
    };
  }

  return { ok: true, value: parsed.data };
}

export function parseTransferMessage(raw: string): ValidationResult<TransferMessage> {
  if (raw.length > MAX_TRANSFER_CONTROL_BYTES * 2) {
    return {
      ok: false,
      error: { code: 'MESSAGE_TOO_LARGE', message: 'Transfer control message is too large.' },
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    return { ok: false, error: { code: 'BAD_REQUEST', message: 'Invalid transfer JSON.' } };
  }

  return validateTransferMessage(parsed);
}

export function serializeTransferMessage(message: TransferMessage): string {
  return JSON.stringify(message);
}

export function createTransferMessage<K extends TransferMessageType>(
  type: K,
  payload: Extract<TransferMessage, { type: K }>['payload'],
): Extract<TransferMessage, { type: K }> {
  return { type, payload } as Extract<TransferMessage, { type: K }>;
}

/* -------------------------------------------------------------------------- */
/* Binary chunk framing                                                       */
/* -------------------------------------------------------------------------- */

const HEX = '0123456789abcdef';

function transferIdToBytes(transferId: string): Uint8Array {
  if (!transferIdSchema.safeParse(transferId).success) {
    throw new Error('Invalid transfer id');
  }
  const bytes = new Uint8Array(16);
  for (let index = 0; index < 16; index += 1) {
    bytes[index] = Number.parseInt(transferId.slice(index * 2, index * 2 + 2), 16);
  }
  return bytes;
}

function bytesToTransferId(bytes: Uint8Array, offset: number): string {
  let result = '';
  for (let index = 0; index < 16; index += 1) {
    const value = bytes[offset + index] ?? 0;
    result += HEX[(value >> 4) & 0x0f];
    result += HEX[value & 0x0f];
  }
  return result;
}

export interface ChunkFrame {
  transferId: string;
  chunkIndex: number;
  payload: Uint8Array;
}

/**
 * Binary layout (big endian, 24 byte header):
 *
 *   0       1 byte   frame version
 *   1       1 byte   frame type (1 = chunk)
 *   2..3    2 bytes  reserved
 *   4..19   16 bytes transfer id
 *   20..23  4 bytes  chunk index
 *   24..    n bytes  file bytes
 */
export function encodeChunkFrame(
  transferId: string,
  chunkIndex: number,
  chunk: Uint8Array,
): ArrayBuffer {
  if (!Number.isInteger(chunkIndex) || chunkIndex < 0 || chunkIndex > 0xffffffff) {
    throw new Error('Invalid chunk index');
  }

  const buffer = new ArrayBuffer(TRANSFER_FRAME_HEADER_BYTES + chunk.byteLength);
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  bytes[0] = TRANSFER_FRAME_VERSION;
  bytes[1] = TRANSFER_CHUNK_FRAME;
  bytes.set(transferIdToBytes(transferId), 4);
  view.setUint32(20, chunkIndex, false);
  bytes.set(chunk, TRANSFER_FRAME_HEADER_BYTES);

  return buffer;
}

export function decodeChunkFrame(buffer: ArrayBuffer | ArrayBufferView): ChunkFrame | null {
  const bytes =
    buffer instanceof ArrayBuffer
      ? new Uint8Array(buffer)
      : new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength);

  if (bytes.byteLength < TRANSFER_FRAME_HEADER_BYTES) return null;
  if (bytes[0] !== TRANSFER_FRAME_VERSION) return null;
  if (bytes[1] !== TRANSFER_CHUNK_FRAME) return null;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const chunkIndex = view.getUint32(20, false);

  return {
    transferId: bytesToTransferId(bytes, 4),
    chunkIndex,
    payload: bytes.subarray(TRANSFER_FRAME_HEADER_BYTES),
  };
}
